/**
 * Cliente HTTP da API da Rueda. Em dev o Vite encaminha /api para a porta 3080;
 * em produção a própria API serve o front, então a origem é a mesma.
 */
const BASE = '/api';
const TOKEN_KEY = 'rdlf.token';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly details?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  get: (): string | null => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  set: (token: string): void => { try { localStorage.setItem(TOKEN_KEY, token); } catch { /* modo privado */ } },
  clear: (): void => { try { localStorage.removeItem(TOKEN_KEY); } catch { /* modo privado */ } },
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

let onUnauthorized: (() => void) | null = null;
/** A sessão registra aqui o que fazer quando um 401 chega fora do login. */
export const setUnauthorizedHandler = (fn: () => void): void => { onUnauthorized = fn; };

export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(BASE + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'Sem conexão com o servidor');
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();
    const msg = extractMessage(data) ?? `Erro ${res.status}`;
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

export const get    = <T>(path: string): Promise<T> => api<T>('GET', path);
export const post   = <T>(path: string, body?: unknown): Promise<T> => api<T>('POST', path, body);
export const patch  = <T>(path: string, body?: unknown): Promise<T> => api<T>('PATCH', path, body);
export const remove = <T = void>(path: string): Promise<T> => api<T>('DELETE', path);

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

/** Nest devolve `{ message: string | string[] }`. */
function extractMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const m = (data as { message?: unknown }).message;
  if (Array.isArray(m)) return m.join(' · ');
  return typeof m === 'string' ? m : null;
}
